import { Phone, Menu, X, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const LOGO_URL = "/logo.webp";

export const Header = ({ onOpenLeadForm, onOpenEstimateForm, cartItems = [], onOpenCart }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <header 
      className="bg-white shadow-sm sticky top-0 z-50"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 xl:px-12">
        <div className="flex items-center justify-between gap-4 h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Appliance Direct" 
              className="h-10 md:h-14 lg:h-12 xl:h-14 w-auto"
              data-testid="logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-5">
            <Link 
              to="/installation"
              className="text-gray-800 hover:text-emerald-600 transition-colors font-bold whitespace-nowrap text-sm xl:text-base"
              data-testid="nav-installation"
            >
              Installation
            </Link>
            <Link 
              to="/unit-only"
              className="text-gray-800 hover:text-emerald-600 transition-colors font-bold whitespace-nowrap text-sm xl:text-base"
              data-testid="nav-unit-only"
            >
              Unit Only
            </Link>
            <Link 
              to="/why-goodman"
              className="text-gray-800 hover:text-emerald-600 transition-colors font-bold whitespace-nowrap text-sm xl:text-base"
              data-testid="nav-why-goodman"
            >
              Why Goodman
            </Link>
            <Link 
              to="/sizing-chart"
              className="text-gray-800 hover:text-emerald-600 transition-colors font-bold whitespace-nowrap text-sm xl:text-base"
              data-testid="nav-sizing-chart"
            >
              Sizing Chart
            </Link>
            <a 
              href="https://www.appliancedirect.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 hover:text-emerald-600 transition-colors font-bold whitespace-nowrap text-sm xl:text-base"
              data-testid="nav-main-site"
            >
              Main Website
            </a>
            {cartCount > 0 && (
              <button
                onClick={onOpenCart}
                className="relative text-emerald-600 hover:text-emerald-700 transition-colors"
                data-testid="header-cart-btn"
                aria-label="View cart"
              >
                <ShoppingCart className="w-6 h-6" />
                <span className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              </button>
            )}
            <button
              onClick={onOpenEstimateForm || onOpenLeadForm}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 xl:px-5 py-2.5 rounded-lg font-bold transition-colors flex items-center gap-2 ml-2 whitespace-nowrap text-sm xl:text-base"
              data-testid="header-cta-button"
            >
              <Phone className="w-4 h-4" />
              Get Free Estimate
            </button>
          </nav>

          {/* Mobile Cart + Menu */}
          <div className="flex lg:hidden items-center gap-3">
            {cartCount > 0 && (
              <button
                onClick={onOpenCart}
                className="relative text-emerald-600 p-2"
                data-testid="mobile-header-cart-btn"
                aria-label="View cart"
              >
                <ShoppingCart className="w-6 h-6" />
                <span className="absolute -top-0.5 -right-0.5 bg-emerald-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              </button>
            )}
            <button 
              className="p-2 text-gray-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <nav className="flex flex-col gap-3">
              <Link 
                to="/installation"
                className="text-gray-800 hover:text-emerald-600 transition-colors font-bold py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Installation
              </Link>
              <Link 
                to="/unit-only"
                className="text-gray-800 hover:text-emerald-600 transition-colors font-bold py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Unit Only
              </Link>
              <Link 
                to="/why-goodman"
                className="text-gray-800 hover:text-emerald-600 transition-colors font-bold py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Why Goodman
              </Link>
              <Link 
                to="/sizing-chart"
                className="text-gray-800 hover:text-emerald-600 transition-colors font-bold py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sizing Chart
              </Link>
              <a 
                href="https://www.appliancedirect.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 hover:text-emerald-600 transition-colors font-bold py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Main Website
              </a>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  (onOpenEstimateForm || onOpenLeadForm)();
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 w-full mt-2"
                data-testid="mobile-cta-button"
              >
                <Phone className="w-4 h-4" />
                Get Free Estimate
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
